import { useDefinitionsActivitiesAndCategoriesQuery } from "@Components/shared/queries";
import { IActivityCategoryDefinition, IActivityDefinition } from "@Models/IActivityDetails";
import { buildTree, ITreeItem } from "@Utils";

export const useActivityCategoriesTree = (
    productId = "",
    disabledActivities?: (record: IActivityDefinition) => boolean
): ITreeItem[] => {
    const { activitiesMapByCategory, activityDefinitions, categoryIds, parentCategoryIds } =
        useDefinitionsActivitiesAndCategoriesQuery();

    const parentCategoriesFilter = (category: IActivityCategoryDefinition) =>
        parentCategoryIds.has(category?.activityCategoryId) && (productId === category?.productId || productId === "");

    const activityTree = (activityCategoryId: IActivityCategoryDefinition["activityCategoryId"]) => {
        if (!activitiesMapByCategory) return [];
        const activityData = activitiesMapByCategory.get(activityCategoryId) || [];
        if (!activityData.length) return [];

        return buildTree<IActivityDefinition>({
            data: activityData,
            fields: {
                title: "label",
                value: "activityId",
                disabled: disabledActivities ? disabledActivities : false,
            },
        });
    };

    const categoryTree = ({ activityCategoryId }: IActivityCategoryDefinition) => {
        if (!activitiesMapByCategory) return [];
        const isValidCategory = categoryIds.has(activityCategoryId);
        if (!isValidCategory) return [];

        const subCategories = activityDefinitions.categories.filter(
            (category) => category.parentCategoryId === activityCategoryId
        );

        if (subCategories.length === 0) return activityTree(activityCategoryId);

        return buildTree<IActivityCategoryDefinition>({
            data: subCategories,
            fields: {
                children: ({ activityCategoryId: id }) => activityTree(id),
                prefix: "subcat-",
                title: "label",
                value: "activityCategoryId",
            },
        });
    };

    const activityCategoriesTree = buildTree<IActivityCategoryDefinition>({
        data: activityDefinitions?.categories?.filter(parentCategoriesFilter),
        fields: { title: "label", prefix: "cat-", value: "activityCategoryId", children: categoryTree },
    });

    return activityCategoriesTree;
};
